// components/calendar/CalendarGrid.js
// Griglia calendario mensile con festività italiane e domeniche evidenziate

import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Building, Star } from 'lucide-react';
import { format, eachDayOfInterval, startOfMonth, endOfMonth, isSameDay } from 'date-fns';
import { it } from 'date-fns/locale';
import { getAppointmentTypeLabel, getAppointmentVenue } from './calendarEventFormatter';
import { isItalianHoliday, isSunday, isSaturday, createHolidayMap, formatDateKey } from '../../utils/italianHolidays';

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

const CalendarGrid = ({
  currentMonth,
  selectedDate,
  setSelectedDate,
  appointments,
  leads,
  getAppointmentTypeColor,
  getRoomName
}) => {
  const days = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth)
  });

  // Mappa delle festività per il mese corrente e successivo (per le transizioni)
  const holidayMap = useMemo(() => {
    const year = currentMonth.getFullYear();
    return {
      ...createHolidayMap(year),
      ...createHolidayMap(year + 1),
      ...createHolidayMap(year - 1)
    };
  }, [currentMonth]);

  const getAppointmentsForDay = (date) => {
    return appointments.filter(apt => 
      isSameDay(new Date(apt.appointment_date), date)
    ).sort((a, b) => new Date(a.appointment_date) - new Date(b.appointment_date));
  };

  // Costruisci nome breve per vista mese: [Nome Abbreviato]
  // Es: "Mario R." oppure "Riunione sett."
  const buildShortName = (apt) => {
    // Per eventi Google, usa titolo breve
    if (apt.source === 'google') {
      return apt.title?.substring(0, 15) || 'Google';
    }
    
    // Per eventi personali/interni
    if (['personale', 'interno'].includes(apt.appointment_type)) {
      const title = apt.title || apt.notes?.substring(0, 12) || '';
      return title ? title.substring(0, 15) : apt.appointment_type === 'personale' ? 'Pers.' : 'Int.';
    }
    
    // Per eventi con lead
    if (apt.lead_id) {
      const lead = leads.find(l => l.id === apt.lead_id);
      if (lead) {
        // Usa nome abbreviato: "Mario R."
        const firstName = lead.first_name || '';
        const lastInitial = lead.last_name ? `${lead.last_name.charAt(0)}.` : '';
        return `${firstName} ${lastInitial}`.trim() || 'Cliente';
      }
    }
    
    return apt.title?.substring(0, 15) || '';
  };

  // Ottieni informazioni speciali sul giorno (festività, domenica, sabato)
  const getDayInfo = (date) => {
    const dateKey = formatDateKey(date);
    const holiday = holidayMap[dateKey];
    const sunday = isSunday(date);
    const saturday = isSaturday(date);
    
    return { holiday, sunday, saturday };
  };

  return (
    <Card className="card-abitalo lg:col-span-2">
      <CardHeader className="p-4 border-b border-[#DCDCDC]">
        <CardTitle className="text-h4">
          {format(currentMonth, 'MMMM yyyy', { locale: it })}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4">
        {/* Weekday headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {WEEKDAYS.map((day, index) => (
            <div 
              key={day} 
              className={`text-center text-xs font-medium py-2 ${
                index === 6 ? 'text-red-500' : index === 5 ? 'text-orange-500' : 'text-[#737373]'
              }`}
            >
              {day}
            </div>
          ))}
        </div>
        
        {/* Calendar days */}
        <div className="grid grid-cols-7 gap-1" data-testid="calendar-grid">
          {/* Empty cells for days before the first of the month */}
          {Array.from({ length: (days[0].getDay() + 6) % 7 }).map((_, i) => (
            <div key={`empty-${i}`} className="h-24 rounded-lg bg-[#FAFAFA]/50" />
          ))}
          
          {days.map(day => {
            const dayAppointments = getAppointmentsForDay(day);
            const isToday = isSameDay(day, new Date());
            const isSelected = selectedDate && isSameDay(day, selectedDate);
            const { holiday, sunday, saturday } = getDayInfo(day);
            
            // Determina lo stile del giorno in base al tipo
            let dayBgClass = 'border-gray-100 hover:border-[#DCDCDC] hover:bg-[#FAFAFA]';
            let dayTextClass = 'text-[#3C3C3C]';
            
            if (holiday) {
              dayBgClass = 'border-red-200 bg-red-50/70 hover:bg-red-100/70';
              dayTextClass = 'text-red-700';
            } else if (sunday) {
              dayBgClass = 'border-red-100 bg-red-50/40 hover:bg-red-100/50';
              dayTextClass = 'text-red-600';
            } else if (saturday) {
              dayBgClass = 'border-orange-100 bg-orange-50/30 hover:bg-orange-100/40';
              dayTextClass = 'text-orange-600';
            }
            
            if (isToday) {
              dayBgClass = 'border-[#FF7819] bg-[#FFF0E5]/50 shadow-sm';
            } else if (isSelected) {
              dayBgClass = 'border-[#0078C8] bg-[#E5F4FF]/50 shadow-sm';
            }
            
            return (
              <div
                key={day.toISOString()}
                className={`min-h-28 rounded-xl border p-2 cursor-pointer transition-all ${dayBgClass}`}
                onClick={() => setSelectedDate(day)}
                data-testid={`calendar-day-${format(day, 'yyyy-MM-dd')}`}
                title={holiday ? `${holiday.name} - Chiuso` : sunday ? 'Domenica - Chiuso' : ''}
              >
                <div className="flex items-start justify-between mb-1">
                  <div className={`text-sm font-semibold ${
                    isToday ? 'text-white bg-[#FF7819] w-7 h-7 rounded-full flex items-center justify-center' : 
                    holiday ? 'text-red-700' : 
                    sunday ? 'text-red-600' : 
                    saturday ? 'text-orange-600' : 
                    'text-[#3C3C3C]'
                  }`}>
                    {format(day, 'd')}
                  </div>
                  {holiday && (
                    <Star className="w-4 h-4 text-red-500 fill-red-500" />
                  )}
                </div>
                
                {/* Holiday name */}
                {holiday && (
                  <div className="text-[10px] font-medium text-red-600 mb-1 truncate" title={holiday.name}>
                    {holiday.name}
                  </div>
                )}
                
                {/* Sunday indicator */}
                {sunday && !holiday && (
                  <div className="text-[10px] font-medium text-red-500 mb-1">
                    Chiuso
                  </div>
                )}
                
                <div className="space-y-1 overflow-hidden">
                  {dayAppointments.slice(0, holiday || sunday ? 2 : 3).map(apt => {
                    const shortName = buildShortName(apt);
                    const hasRoom = apt.room_id;
                    
                    // Tooltip completo (venue snapshot-aware)
                    const lead = apt.lead_id ? leads.find(l => l.id === apt.lead_id) : null;
                    const fullName = lead ? `${lead.first_name || ''} ${lead.last_name || ''}`.trim() : apt.title;
                    const venue = getAppointmentVenue(apt, lead);
                    const tooltipParts = [format(new Date(apt.appointment_date), 'HH:mm')];
                    if (apt.appointment_type) tooltipParts.push(getAppointmentTypeLabel(apt.appointment_type));
                    if (venue?.name) tooltipParts.push(venue.name);
                    if (venue?.city) tooltipParts.push(venue.city);
                    if (fullName) tooltipParts.push(fullName);
                    if (hasRoom) tooltipParts.push(`(${getRoomName(apt.room_id)})`);
                    if (apt.source === 'google') tooltipParts.push('(Google)');
                    
                    return (
                      <div
                        key={apt.id}
                        className={`text-xs px-2 py-1 rounded-md truncate font-medium text-white flex items-center gap-1 ${getAppointmentTypeColor(apt.appointment_type, apt.source)}`}
                        title={tooltipParts.join(' - ')}
                      >
                        {hasRoom && <Building className="w-3 h-3 flex-shrink-0" />}
                        <span className="truncate">{format(new Date(apt.appointment_date), 'HH:mm')} {shortName}</span>
                      </div>
                    );
                  })}
                  {dayAppointments.length > (holiday || sunday ? 2 : 3) && (
                    <div className="text-[11px] text-[#737373] font-medium pl-1">
                      +{dayAppointments.length - (holiday || sunday ? 2 : 3)} altri
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Color Legend */}
        <div className="mt-4 pt-4 border-t border-[#DCDCDC]">
          <p className="text-xs text-[#737373] mb-2 font-medium">Legenda:</p>
          <div className="flex flex-wrap gap-3">
            {/* Appointment types */}
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-[#0078C8]" />
              <span className="text-xs text-[#737373]">Sopralluogo</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-[#FF7819]" />
              <span className="text-xs text-[#737373]">Sede</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-[#6B7280]" />
              <span className="text-xs text-[#737373]">Interno</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-[#10B981]" />
              <span className="text-xs text-[#737373]">Scelta fornitore</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-[#DC2626]" />
              <span className="text-xs text-[#737373]">Scadenza</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-[#9333EA]" />
              <span className="text-xs text-[#737373]">Google</span>
            </div>
            
            {/* Separator */}
            <div className="w-px h-4 bg-[#DCDCDC] mx-1" />
            
            {/* Day types */}
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded border border-red-200 bg-red-50" />
              <Star className="w-3 h-3 text-red-500 fill-red-500" />
              <span className="text-xs text-[#737373]">Festività</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded border border-red-100 bg-red-50/40" />
              <span className="text-xs text-red-600">Dom</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded border border-orange-100 bg-orange-50/30" />
              <span className="text-xs text-orange-600">Sab</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CalendarGrid;
